// What the Download footage button does, as pure decisions (FootagePullButton.js draws them).
//
// Ryan, 2026-10-02: on a Mac without Footage Pull, the click fired footagepull:// straight away, macOS answered with
// its own "There is no application set to open the URL" dialog, and the portal never offered the download. The
// browser remembered that a hand-off once worked there (old test builds, since deleted), so after the wait it showed
// one quiet line with no download buttons. Two rules came out of it:
//   1. A browser where the hand-off has never worked does NOT fire the scheme on the first click. It shows the setup
//      panel at once, and the panel has its own "I've installed it" button that fires it.
//   2. When the remembered hand-off does not confirm in time, the memory is stale: forget it, and offer the
//      download right there.

export const OK_KEY = 'ug_footagepull_opened';

// A click on Download footage: 'fire' the scheme (the app has opened from this browser before), or show the
// 'panel' first (it never has, so firing now only gets the OS's "no application" dialog on a computer without it).
export function clickAction(workedBefore) {
  return workedBefore ? 'fire' : 'panel';
}

// The app confirmed when footage-status shows it opened, or issued a token, after the click. ISO strings compare
// in time order.
export function confirmedSince(status, clickedAt) {
  return [status?.opened_at, status?.token_issued_at].filter(Boolean).some(t => t > clickedAt);
}

// Nothing confirmed inside the window. `from` is where the open was asked for: the main 'button', the setup
// 'panel', or the 'quiet' line's Try again. Returns the box to show, whether to say "it didn't open" inside it,
// and whether to forget the remembered hand-off (always: whatever this browser remembered did not open the app).
export function afterNoConfirm({ workedBefore, from }) {
  if (from === 'panel') return { view: 'panel', missed: true, forget: true };
  if (from === 'quiet') return { view: 'quiet', missed: true, forget: true };
  return { view: workedBefore ? 'quiet' : 'panel', missed: false, forget: true };
}

// The editors' "a new Footage Pull is out" banner (Ryan, 2026-10-10: "maybe have a dismissable banner saying new
// footage pull 2.13 update available on the editor side"). Layout draws it; FootagePullUpdate.js holds it.
export const BANNER_KEY = 'ug_footagepull_banner_dismissed';
export const SELF_UPDATING_FROM = '2.13';   // the first Footage Pull with an Update button (backend/tools/footage_pull_update.py)

// '2.12' -> [2, 12]; '2.12-test' -> [2, 12]; anything without leading numbers -> null (as app/footage_app_version.py).
export function versionParts(v) {
  const m = String(v ?? '').match(/^\s*(\d+(?:\.\d+)*)/);
  return m ? m[1].split('.').map(Number) : null;
}

// -1, 0 or 1 by number, part by part ('2.9' < '2.12'); null when either cannot be read.
export function compareVersions(a, b) {
  const x = versionParts(a), y = versionParts(b);
  if (!x || !y) return null;
  for (let i = 0; i < Math.max(x.length, y.length); i++) {
    const d = (x[i] || 0) - (y[i] || 0);
    if (d) return d < 0 ? -1 : 1;
  }
  return 0;
}

// The newest version published for any platform (the public version endpoint's `builds`), or null.
export function latestPublished(builds) {
  let best = null;
  for (const b of Object.values(builds || {})) {
    if (b?.version && compareVersions(b.version, best ?? '0') === 1) best = b.version;
  }
  return best;
}

// Who is told a newer Footage Pull is out: editors, and the admin and staff who pull footage with it too (Ryan,
// 2026-10-22: admin had nowhere in the portal to get the new one). Clients and billing never use it.
export const BANNER_ROLES = ['editor', 'admin', 'staff'];

// What the banner says, or null for none. `yours`: the copy this person last used (app-status; null when they never
// used it: they download the newest anyway). `dismissed`: the version they closed the banner for; a newer one shows
// again. `hasButton`: their copy can update itself, so the banner points at its Update button, not a download.
export function bannerFor({ role, yours, latest, dismissed }) {
  if (!BANNER_ROLES.includes(role) || !yours || !latest) return null;
  if (compareVersions(yours, latest) !== -1) return null;
  if (dismissed && compareVersions(dismissed, latest) !== -1) return null;
  // latestUpdatesItself: only then may the banner promise "from 2.13 on, it updates itself" (before 2.13 is published
  // the newest is 2.12, which cannot).
  return { latest, yours, hasButton: compareVersions(yours, SELF_UPDATING_FROM) !== -1,
           latestUpdatesItself: compareVersions(latest, SELF_UPDATING_FROM) !== -1 };
}
